/**
 * Custom pdfme schema for rendering 2D interior layout preview
 * Uses actual TrayPanel parts from partDefinitions for accurate representation
 */

import { Sides } from "@project/emerge-types";

// Golden angle for color distribution
const PHI = (1 + Math.sqrt(5)) / 2;
const GOLDEN_ANGLE = 360 * (1 - 1 / PHI);

/** Generate a pastel color using golden angle (returns RGB 0-1 values) */
function goldenAngleColor(index: number): { r: number; g: number; b: number } {
  const hue = (index * GOLDEN_ANGLE) % 360;
  // Convert HSL to RGB (S=65%, L=82%)
  const s = 0.65;
  const l = 0.82;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0,
    g = 0,
    b = 0;
  if (hue < 60) {
    r = c;
    g = x;
  } else if (hue < 120) {
    r = x;
    g = c;
  } else if (hue < 180) {
    g = c;
    b = x;
  } else if (hue < 240) {
    g = x;
    b = c;
  } else if (hue < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  return { r: r + m, g: g + m, b: b + m };
}

/** Create dimension key for grouping identical sizes */
function dimensionKey(w: number, h: number): string {
  return `${Math.round(w)}×${Math.round(h)}`;
}

/**
 * Convert hex color string to RGB values (0-1 range)
 * Supports both 3-digit (#000) and 6-digit (#000000) hex colors
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  // Remove # if present
  let cleanHex = hex.replace("#", "");

  // Support both 3-digit (#000) and 6-digit (#000000) hex colors
  // Expand 3-digit to 6-digit: #abc -> #aabbcc
  if (cleanHex.length === 3) {
    const c0 = cleanHex[0];
    const c1 = cleanHex[1];
    const c2 = cleanHex[2];
    if (!c0 || !c1 || !c2) {
      throw new Error(`Invalid hex color: ${hex} (incomplete 3-digit color)`);
    }
    cleanHex = c0 + c0 + c1 + c1 + c2 + c2;
  }

  // Ensure we have exactly 6 hex characters
  if (cleanHex.length !== 6) {
    throw new Error(
      `Invalid hex color: ${hex} (expected 3 or 6 hex digits, got ${cleanHex.length})`,
    );
  }

  // Parse hex values
  const r = Number.parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = Number.parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = Number.parseInt(cleanHex.substring(4, 6), 16) / 255;

  // Validate that all values are valid numbers
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) {
    throw new Error(`Invalid hex color: ${hex} (parsed to NaN)`);
  }

  return { r, g, b };
}

/** Panel data for 2D rendering (supports both TrayPanels and UnderlayPanels) */
export interface PanelData {
  position: { x: number; y: number; z: number };
  size: { x: number; y: number }; // Vector2 - width, height of the panel
  side?: number; // Sides enum value (BOTTOM = horizontal, RIGHT = vertical)
  strength: number;
  orientation?: number; // Rotation in degrees within the panel's plane
  color?: { r: number; g: number; b: number }; // RGB color from panel material (0-1 range)
}

/** Compartment volume data for 2D rendering */
export interface CompartmentVolumeData {
  position: { x: number; y: number; z: number };
  size: [number, number, number];
  side?: number; // Sides enum value
}

/** Pad (Schaumstoff) data for 2D rendering */
export interface PadData {
  position: { x: number; y: number; z: number };
  size: [number, number]; // 2D size
  strength: number; // thickness/depth
  side?: number;
}

/** Interior layout data passed as JSON string */
export interface InteriorLayoutData {
  panels: PanelData[];
  volumes?: CompartmentVolumeData[];
  pads?: PadData[];
  innerDimensions?: { x: number; y: number; z: number };
  viewDirection?: Sides; // Direction to view from (default: Sides.TOP)
}

/** Rectangle for rendering */
export interface RenderRect {
  x: number;
  y: number;
  w: number;
  h: number;
  color: { r: number; g: number; b: number };
  isPanel?: boolean;
  zLayer?: number; // Depth coordinate for layering (the third coordinate from 3D)
  labels?: string[]; // Optional labels (e.g. dimensions)
}

interface BoundingBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function updateBounds(
  bounds: BoundingBox,
  x: number,
  y: number,
  w: number,
  h: number,
) {
  bounds.minX = Math.min(bounds.minX, x);
  bounds.minY = Math.min(bounds.minY, y);
  bounds.maxX = Math.max(bounds.maxX, x + w);
  bounds.maxY = Math.max(bounds.maxY, y + h);
}

/**
 * Calculate 2D rectangle from 3D object properties based on view direction
 */
function getProjectedRect(
  center: { x: number; y: number; z: number },
  size: { x: number; y: number; z: number }, // Global dimensions: x=width, y=height, z=depth/strength
  viewDirection: Sides,
): { x: number; y: number; w: number; h: number; zLayer: number } {
  let x = 0;
  let y = 0;
  let w = 0;
  let h = 0;
  let zLayer = 0;

  if (viewDirection === Sides.TOP) {
    // Top view: X, Z → 2D; Y → zLayer
    w = size.x;
    h = size.z;
    x = center.x - w / 2;
    y = center.z - h / 2;
    zLayer = center.y;
  } else if (viewDirection === Sides.BACK) {
    // Back view: X, Y → 2D (Z inverted); Z → zLayer
    w = size.x;
    h = size.y;
    x = center.x - w / 2;
    y = center.y - h / 2;
    zLayer = center.z;
  } else if (viewDirection === Sides.LEFT) {
    // Left view: Z, Y → 2D; X → zLayer
    w = size.z;
    h = size.y;
    x = center.z - w / 2;
    y = -center.y - h / 2; // Invert Y
    zLayer = center.x;
  } else if (viewDirection === Sides.RIGHT) {
    // Right view: Z, Y → 2D (X inverted); X → zLayer
    w = size.z;
    h = size.y;
    x = -(center.z + w / 2); // Invert Z
    y = -center.y - h / 2; // Invert Y
    zLayer = center.x;
  } else {
    // Front view (default): X, Y → 2D; Z → zLayer
    w = size.x;
    h = size.y;
    x = center.x - w / 2;
    y = -center.y - h / 2; // Invert Y
    zLayer = center.z;
  }

  return { x, y, w, h, zLayer };
}

/**
 * Apply in-plane rotation to size dimensions
 * Orientation is in degrees, rotating within the panel's plane
 * For simplicity, we handle 90° increments (0, 90, 180, 270)
 */
function applyOrientationToSize(
  size: { x: number; y: number },
  orientation: number,
): { x: number; y: number } {
  // Normalize orientation to 0-360
  const normalizedOrientation = ((orientation % 360) + 360) % 360;

  // For 90° or 270° rotations, swap x and y
  if (
    (normalizedOrientation >= 45 && normalizedOrientation < 135) ||
    (normalizedOrientation >= 225 && normalizedOrientation < 315)
  ) {
    return { x: size.y, y: size.x };
  }

  // For 0° or 180° rotations, keep dimensions as-is
  return { x: size.x, y: size.y };
}

/**
 * Determine global dimensions of a volume or panel based on its side orientation
 * Returns { x: width, y: height, z: depth } in global coordinates
 */
function getGlobalDimensions(
  size: { x: number; y: number; z?: number }, // z is optional for panels
  side: number | undefined,
  strength: number, // strength for panels, size.z for volumes
  orientation?: number, // Rotation in degrees within the panel's plane
): { x: number; y: number; z: number } {
  // Apply in-plane rotation if orientation is specified
  const rotatedSize =
    orientation !== undefined
      ? applyOrientationToSize(size, orientation)
      : size;

  // Mapping logic based on side orientation
  if (side === Sides.LEFT || side === Sides.RIGHT) {
    // Side view:
    // Global X (Width) = Normal/Thickness (strength/size.z)
    // Global Y (Height) = Vertical on side (size.y)
    // Global Z (Depth) = Horizontal on side (size.x)
    return {
      x: strength,
      y: rotatedSize.y,
      z: rotatedSize.x,
    };
  } else if (
    side === Sides.FRONT ||
    side === Sides.BACK ||
    side === undefined
  ) {
    // Sides.FRONT || Sides.BACK || undefined (Standard Volume)
    // Front/Back view (Standard) or No Side (Standard Volume):
    // Global X (Width) = Horizontal on side (size.x)
    // Global Y (Height) = Vertical on side (size.y)
    // Global Z (Depth) = Normal/Thickness (strength/size.z)
    return {
      x: rotatedSize.x,
      y: rotatedSize.y,
      z: strength,
    };
  } else {
    // Sides.TOP || Sides.BOTTOM
    // Top/Bottom view:
    // Global X (Width) = Horizontal on side (size.x)
    // Global Y (Height) = Normal/Thickness (strength/size.z)
    // Global Z (Depth) = Vertical on side (size.y)
    return {
      x: rotatedSize.x,
      y: strength,
      z: rotatedSize.y,
    };
  }
}

/**
 * Prepares the size object for a volume based on its side to preserve global dimensions
 * when processed by getGlobalDimensions.
 */
function getVolumeSizeForGlobalDimensions(
  volSize: [number, number, number],
  side: number,
): { x: number; y: number; z: number } {
  if (side === Sides.LEFT || side === Sides.RIGHT) {
    // Global X corresponds to Thickness (z/strength)
    // Global Y corresponds to Height (y)
    // Global Z corresponds to Width (x)
    // vol.size is [x, y, z] -> We want G=[x, y, z]
    // Gx = Strength.  => Strength = vol.size[0]
    // Gy = SizeY.     => SizeY = vol.size[1]
    // Gz = SizeX.     => SizeX = vol.size[2]
    return { x: volSize[2], y: volSize[1], z: volSize[0] };
  }

  if (side === Sides.TOP || side === Sides.BOTTOM) {
    // Global X corresponds to Width (x)
    // Global Y corresponds to Thickness (z/strength)
    // Global Z corresponds to Height (y)
    // vol.size is [x, y, z] -> We want G=[x, y, z]
    // Gx = SizeX.     => SizeX = vol.size[0]
    // Gy = Strength.  => Strength = vol.size[1]
    // Gz = SizeY.     => SizeY = vol.size[2]
    return { x: volSize[0], y: volSize[2], z: volSize[1] };
  }

  // Front/Back
  // Gx = SizeX.     => SizeX = vol.size[0]
  // Gy = SizeY.     => SizeY = vol.size[1]
  // Gz = Strength.  => Strength = vol.size[2]
  return { x: volSize[0], y: volSize[1], z: volSize[2] };
}

function getVolumeLabels(
  w: number,
  h: number,
  _globalSize: { x: number; y: number; z: number },
  _viewDirection: Sides,
): string[] {
  // Return only width×height without "mm" and without height line
  return [`${Math.round(w)}×${Math.round(h)}`];
}

function processVolumes(
  volumes: CompartmentVolumeData[],
  viewDirection: Sides,
  rects: RenderRect[],
  bounds: BoundingBox | null,
) {
  // Build color map for volumes with identical dimensions
  const volumeColorMap = new Map<string, { r: number; g: number; b: number }>();
  let colorIndex = 0;

  for (const vol of volumes) {
    // vol.size is [x, y, z]
    if (!Array.isArray(vol.size) || vol.size.length < 3) continue;
    const w = vol.size[0];
    const h = vol.size[1];
    const key = dimensionKey(w, h);
    if (!volumeColorMap.has(key)) {
      volumeColorMap.set(key, goldenAngleColor(colorIndex++));
    }
  }

  for (const vol of volumes) {
    const center = vol.position;
    if (
      !center ||
      !vol.size ||
      !Array.isArray(vol.size) ||
      vol.size.length < 3 ||
      !Number.isFinite(center.x) ||
      !Number.isFinite(center.y) ||
      !Number.isFinite(center.z) ||
      !Number.isFinite(vol.size[0]) ||
      !Number.isFinite(vol.size[1]) ||
      !Number.isFinite(vol.size[2])
    ) {
      console.warn("[interiorSchema] Invalid volume data, skipping:", vol);
      continue;
    }

    const side = vol.side ?? Sides.TOP; // Default to TOP as requested if undefined

    // Convert array size to object based on side to preserve Global XYZ
    const sizeObj = getVolumeSizeForGlobalDimensions(vol.size, side);
    const globalSize = getGlobalDimensions(sizeObj, side, sizeObj.z); // sizeObj.z is strength
    const { x, y, w, h, zLayer } = getProjectedRect(
      center,
      globalSize,
      viewDirection,
    );

    if (
      !Number.isFinite(x) ||
      !Number.isFinite(y) ||
      !Number.isFinite(w) ||
      !Number.isFinite(h)
    ) {
      console.warn(
        "[interiorSchema] Invalid calculated volume rect, skipping:",
        { x, y, w, h },
      );
      continue;
    }

    const key = dimensionKey(w, h);
    const color = volumeColorMap.get(key) ?? { r: 0.95, g: 0.75, b: 0.75 };

    rects.push({
      x,
      y,
      w,
      h,
      color,
      isPanel: false,
      zLayer,
      labels: getVolumeLabels(w, h, globalSize, viewDirection),
    });

    if (bounds) {
      updateBounds(bounds, x, y, w, h);
    }
  }
}

function processPads(
  pads: PadData[],
  viewDirection: Sides,
  rects: RenderRect[],
  bounds: BoundingBox | null,
) {
  const padColor = { r: 0.7, g: 0.85, b: 0.9 }; // Light blue for padding

  for (const pad of pads) {
    if (
      !pad.position ||
      !pad.size ||
      !Array.isArray(pad.size) ||
      pad.size.length < 2 ||
      !Number.isFinite(pad.position.x) ||
      !Number.isFinite(pad.position.y) ||
      !Number.isFinite(pad.position.z) ||
      !Number.isFinite(pad.size[0]) ||
      !Number.isFinite(pad.size[1]) ||
      (pad.strength !== undefined && !Number.isFinite(pad.strength))
    ) {
      console.warn("[interiorSchema] Invalid pad data, skipping:", pad);
      continue;
    }

    // Pad size is [x, y], strength is z
    const sizeObj = { x: pad.size[0], y: pad.size[1] };
    const strength = pad.strength ?? 0;
    const side = pad.side ?? Sides.TOP; // Default to TOP

    const globalSize = getGlobalDimensions(sizeObj, side, strength);

    const { x, y, w, h, zLayer } = getProjectedRect(
      pad.position,
      globalSize,
      viewDirection,
    );

    if (
      !Number.isFinite(x) ||
      !Number.isFinite(y) ||
      !Number.isFinite(w) ||
      !Number.isFinite(h)
    ) {
      console.warn("[interiorSchema] Invalid calculated pad rect, skipping:", {
        x,
        y,
        w,
        h,
      });
      continue;
    }

    rects.push({
      x,
      y,
      w,
      h,
      color: padColor,
      isPanel: false,
      zLayer,
    });

    if (bounds) {
      updateBounds(bounds, x, y, w, h);
    }
  }
}

/**
 * Process panels for 2D rendering
 * Supports both TrayPanels (dividers) and UnderlayPanels (exterior walls)
 * @param panels - Array of panel data (PanelData - supports both TrayPanels and UnderlayPanels)
 * @param viewDirection - The viewing direction
 * @param rects - Array to collect rendered rectangles
 * @param bounds - Optional bounding box to update
 */
function processPanels(
  panels: PanelData[],
  viewDirection: Sides,
  rects: RenderRect[],
  bounds: BoundingBox | null,
) {
  // Default fallback color
  const defaultExteriorColor = { r: 0.3, g: 0.3, b: 0.3 }; // Dark gray for walls

  for (const panel of panels) {
    if (
      !panel.position ||
      !panel.size ||
      !Number.isFinite(panel.position.x) ||
      !Number.isFinite(panel.position.y) ||
      !Number.isFinite(panel.position.z) ||
      !Number.isFinite(panel.size.x) ||
      !Number.isFinite(panel.strength)
    ) {
      console.warn("[interiorSchema] Invalid panel data, skipping:", panel);
      continue;
    }

    // Use color from panel material, fallback to default
    const panelColor = panel.color ?? defaultExteriorColor;
    const side = panel.side ?? Sides.TOP; // Default to TOP

    const globalSize = getGlobalDimensions(
      panel.size,
      side,
      panel.strength,
      panel.orientation,
    );

    const { x, y, w, h, zLayer } = getProjectedRect(
      panel.position,
      globalSize,
      viewDirection,
    );

    if (
      !Number.isFinite(x) ||
      !Number.isFinite(y) ||
      !Number.isFinite(w) ||
      !Number.isFinite(h)
    ) {
      console.warn("[interiorSchema] Invalid calculated rect, skipping:", {
        x,
        y,
        w,
        h,
      });
      continue;
    }

    rects.push({
      x,
      y,
      w,
      h,
      color: panelColor,
      isPanel: true,
      zLayer,
    });

    if (bounds) {
      updateBounds(bounds, x, y, w, h);
    }
  }
}

/**
 * Calculate render rectangles from interior data
 * Projects 3D positions to 2D based on view direction
 * Y-UP coordinate system: X=horizontal, Y=vertical, Z=depth
 */
export function calculateInteriorRects(data: InteriorLayoutData): {
  rects: RenderRect[];
  totalWidth: number;
  totalHeight: number;
} {
  const {
    panels,
    volumes,
    pads,
    innerDimensions,
    viewDirection = Sides.TOP,
  } = data;

  const rects: RenderRect[] = [];

  if (
    (!panels || panels.length === 0) &&
    (!volumes || volumes.length === 0) &&
    (!pads || pads.length === 0)
  ) {
    console.warn("[interiorSchema] No panels, volumes, or pads to render");
    return { rects: [], totalWidth: 100, totalHeight: 100 };
  }

  let bounds: BoundingBox | null = null;
  let minX: number;
  let minY: number;
  let maxX: number;
  let maxY: number;

  if (innerDimensions) {
    // Project centered box dimensions
    const { w, h } = getProjectedRect(
      { x: 0, y: 0, z: 0 },
      innerDimensions,
      viewDirection,
    );
    minX = -w / 2;
    maxX = w / 2;
    minY = -h / 2;
    maxY = h / 2;
  } else {
    minX = Number.POSITIVE_INFINITY;
    minY = Number.POSITIVE_INFINITY;
    maxX = Number.NEGATIVE_INFINITY;
    maxY = Number.NEGATIVE_INFINITY;
    bounds = { minX, minY, maxX, maxY };
  }

  if (volumes && volumes.length > 0) {
    processVolumes(volumes, viewDirection, rects, bounds);
  }

  if (pads && pads.length > 0) {
    processPads(pads, viewDirection, rects, bounds);
  }

  if (panels.length > 0) {
    processPanels(panels, viewDirection, rects, bounds);
  }

  // Update local vars if bounds were used
  if (bounds) {
    minX = bounds.minX;
    minY = bounds.minY;
    maxX = bounds.maxX;
    maxY = bounds.maxY;
  }

  if (
    rects.length === 0 ||
    !Number.isFinite(minX) ||
    !Number.isFinite(minY) ||
    !Number.isFinite(maxX) ||
    !Number.isFinite(maxY)
  ) {
    console.warn("[interiorSchema] Invalid bounds:", {
      minX,
      minY,
      maxX,
      maxY,
      rectCount: rects.length,
    });
    return { rects: [], totalWidth: 100, totalHeight: 100 };
  }

  const totalWidth = Math.max(1, maxX - minX);
  const totalHeight = Math.max(1, maxY - minY);

  if (!Number.isFinite(totalWidth) || !Number.isFinite(totalHeight)) {
    console.warn("[interiorSchema] Invalid dimensions:", {
      totalWidth,
      totalHeight,
    });
    return { rects: [], totalWidth: 100, totalHeight: 100 };
  }

  for (const rect of rects) {
    rect.x -= minX;
    rect.y -= minY;
  }

  return { rects, totalWidth, totalHeight };
}

export interface DrawContext {
  page: import("pdf-lib").PDFPage;
  baseX: number;
  baseY: number;
  offsetX: number;
  offsetY: number;
  scale: number;
  totalHeight: number;
  minZ: number;
  zRange: number;
  font: import("pdf-lib").PDFFont;
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB;
}

interface LabelRenderOptions {
  page: import("pdf-lib").PDFPage;
  labels: string[];
  rect: { x: number; y: number; w: number; h: number }; // Rendered coordinates
  font: import("pdf-lib").PDFFont;
  rgb: (r: number, g: number, b: number) => import("pdf-lib").RGB;
}

function drawLabels(options: LabelRenderOptions) {
  const { page, labels, rect, font, rgb } = options;
  const { x: rx, y: ry, w: rw, h: rh } = rect;

  // Calculate font size based on rectangle dimensions
  // Base size: fit within width/height, max 12pt (increased from 7pt), min 4pt (increased from 2pt)
  const fontSize = Math.min(12, Math.max(4, Math.min(rw, rh) / 6));

  const lineHeight = fontSize * 1.2;
  const totalTextHeight = labels.length * lineHeight;

  // Start Y position for centering vertically
  // Note: PDF coords Y grows upwards, so we start from center + half height and move down
  let currentY = ry + rh / 2 + totalTextHeight / 2 - fontSize;

  for (const label of labels) {
    const textWidth = font.widthOfTextAtSize(label, fontSize);

    // Only draw if it fits horizontally
    if (textWidth < rw) {
      page.drawText(label, {
        x: rx + (rw - textWidth) / 2,
        y: currentY,
        size: fontSize,
        font: font,
        color: rgb(0.1, 0.1, 0.1),
      });
    }
    currentY -= lineHeight;
  }
}

export async function drawRects(rects: RenderRect[], ctx: DrawContext) {
  const {
    page,
    baseX,
    baseY,
    offsetX,
    offsetY,
    scale,
    totalHeight,
    minZ,
    zRange,
    font,
    rgb,
  } = ctx;

  for (const rect of rects) {
    // Calculate depth offset based on zLayer for visual depth
    // Front elements (larger zLayer) get slight offset to appear in front
    const zLayer = rect.zLayer ?? 0;
    const depthOffset =
      zRange > 0
        ? ((zLayer - minZ) / zRange) * 0.5 // Max 0.5pt offset
        : 0;

    const rx = baseX + offsetX + rect.x * scale + depthOffset;
    // Flip Y for PDF coordinates
    const ry = baseY + offsetY + (totalHeight - rect.y - rect.h) * scale;
    const rw = rect.w * scale;
    const rh = rect.h * scale;

    if (rect.isPanel) {
      // Draw panel (divider or wall)
      page.drawRectangle({
        x: rx,
        y: ry,
        width: rw,
        height: rh,
        color: rgb(rect.color.r, rect.color.g, rect.color.b),
        borderColor: rgb(0.3, 0.2, 0.1),
        borderWidth: 0.3,
      });
    } else {
      // Draw volume (compartment)
      page.drawRectangle({
        x: rx,
        y: ry,
        width: rw,
        height: rh,
        color: rgb(rect.color.r, rect.color.g, rect.color.b),
        borderColor: rgb(0.5, 0.5, 0.5),
        borderWidth: 0.2,
      });

      // Draw labels if available and volume is large enough
      if (rect.labels && rect.labels.length > 0) {
        drawLabels({
          page,
          labels: rect.labels,
          rect: { x: rx, y: ry, w: rw, h: rh },
          font,
          rgb,
        });
      }
    }
  }
}

/**
 * pdfme custom schema plugin for interior layout
 * Uses pdf-lib directly for rendering rectangles
 */
// biome-ignore lint/suspicious/noExplicitAny: pdfme Plugin type is complex
export const interiorLayout: any = {
  ui: (arg: { rootElement: HTMLElement; value: string }) => {
    const div = document.createElement("div");
    div.style.width = "100%";
    div.style.height = "100%";
    div.style.backgroundColor = "transparent";
    div.style.display = "flex";
    div.style.alignItems = "center";
    div.style.justifyContent = "center";
    div.style.fontSize = "10px";
    div.style.color = "#666";
    div.textContent = "Interior Layout Preview";
    arg.rootElement.appendChild(div);
  },

  pdf: async (arg: {
    pdfDoc: import("pdf-lib").PDFDocument;
    page: import("pdf-lib").PDFPage;
    schema: {
      position: { x: number; y: number };
      width: number;
      height: number;
    };
    value: string;
  }) => {
    const { pdfDoc, page, schema, value } = arg;
    const { rgb, StandardFonts } = await import("pdf-lib");

    // Parse interior data from JSON string
    let data: InteriorLayoutData = { panels: [] };
    try {
      if (value && typeof value === "string" && value.length > 2) {
        data = JSON.parse(value);
      }
    } catch (e) {
      console.warn("Failed to parse interior data:", e);
      return;
    }

    // Calculate rectangles
    const { rects, totalWidth, totalHeight } = calculateInteriorRects(data);

    if (rects.length === 0) {
      return; // Nothing to render
    }

    // Sort by zLayer (back to front) so front elements are drawn on top
    // If zLayer is undefined, treat as 0 (front)
    const sortedRects = [...rects].sort((a, b) => {
      const zA = a.zLayer ?? 0;
      const zB = b.zLayer ?? 0;
      return zA - zB; // Back (smaller) first, front (larger) last
    });

    // Calculate zLayer range for depth visualization
    const zLayers = sortedRects
      .map((r) => r.zLayer)
      .filter((z): z is number => z !== undefined);
    const minZ = zLayers.length > 0 ? Math.min(...zLayers) : 0;
    const maxZ = zLayers.length > 0 ? Math.max(...zLayers) : 0;
    const zRange = Math.max(1, maxZ - minZ); // Avoid division by zero

    // Get position and size from schema
    const { x, y } = schema.position;
    const { width, height } = schema;

    // mm to points conversion (1mm = 2.83465 points)
    const MM_TO_PT = 2.83465;

    // Calculate scale to fit content in the box
    const scaleX = (width * MM_TO_PT) / totalWidth;
    const scaleY = (height * MM_TO_PT) / totalHeight;
    const scale = Math.min(scaleX, scaleY) * 0.9; // 90% to leave margin

    // Center offset
    const offsetX = (width * MM_TO_PT - totalWidth * scale) / 2;
    const offsetY = (height * MM_TO_PT - totalHeight * scale) / 2;

    // PDF coordinates: origin at bottom-left, y increases upward
    const pageHeight = page.getHeight();
    const baseX = x * MM_TO_PT;
    const baseY = pageHeight - (y + height) * MM_TO_PT;

    // No background or border - render only the interior components

    // Embed font for labels
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

    await drawRects(sortedRects, {
      page,
      baseX,
      baseY,
      offsetX,
      offsetY,
      scale,
      totalHeight,
      minZ,
      zRange,
      font,
      rgb,
    });
  },

  propPanel: {
    schema: {},
    defaultSchema: {
      name: "interiorLayout",
      type: "interiorLayout",
      position: { x: 0, y: 0 },
      width: 190,
      height: 60,
    },
    defaultValue: "",
  },
};

export default interiorLayout;
